import React, { useState } from 'react'
import { withTranslation } from 'react-i18next'
import { useMutation, useQuery, gql } from '@apollo/client'
import { keyframes } from '@emotion/react'
import { reviews, deleteReview } from '../apollo'
import {
  Box, Typography, Paper, TextField, Avatar,
  InputAdornment, Chip, CircularProgress
} from '@mui/material'
import StarRoundedIcon from '@mui/icons-material/StarRounded'
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded'
import SearchIcon from '@mui/icons-material/Search'
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined'
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined'
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined'
import {
  ops, opsCssVars, FS, RADIUS, SHADOW, EASE, tabular, labelSx, fadeUp
} from '../components/Order/opsTokens'

const REVIEWS = gql`${reviews}`
const DELETE_REVIEW = gql`${deleteReview}`

// Who is signed in, for the super-admin-only Remove action.
const signedInType = () => {
  try { return JSON.parse(localStorage.getItem('user-enatega') || '{}').userType } catch (e) { return null }
}

// Neutrals, type scale, radius and motion come from the shared opsTokens.
// These are the page's own accents: indigo, amber stars, green for 5 star and
// red for 1-2 star. `fg` is the shade that clears 4.5:1 on its `bg` tint; the
// large counts keep the brighter `value` shade (AA large text, 3:1).
const STAR = '#f59e0b'
const STAR_EMPTY = '#98A2B3'
const ACCENT = {
  solid: '#818cf8',
  strong: '#6366f1',
  text: '#4338ca',
  soft: '#EEF2FF',
  softBorder: '#E0E7FF',
  ring: '0 0 0 3px rgba(99, 102, 241, 0.18)'
}
const NEUTRAL_TONE = { bg: ops.sunken, fg: ops.muted }

const PAGE_WIDTH = { width: '100%', maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 4 }, boxSizing: 'border-box' }
const CARD = {
  bgcolor: ops.surface,
  border: `1px solid ${ops.border}`,
  borderRadius: RADIUS.card,
  boxShadow: SHADOW.pop,
  p: { xs: 2, sm: 2.5 },
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0
}
// Every stat card puts its value in a row of the same height, bottom-aligned,
// so the numbers share a line across the row of cards.
const VALUE_ROW = { display: 'flex', alignItems: 'flex-end', height: { xs: 44, md: 52 }, mt: 1.5 }
const cardIn = i => ({ animation: `${fadeUp} 420ms ${EASE} both`, animationDelay: `${i * 50}ms` })

const grow = keyframes`
  from { transform: scaleX(0); }
  to { transform: scaleX(1); }
`

const StarRating = ({ rating, size = 16 }) => (
  <Box sx={{ display: 'flex', gap: '1px' }}>
    {[1, 2, 3, 4, 5].map(i =>
      i <= rating
        ? <StarRoundedIcon key={i} sx={{ fontSize: size, color: STAR }} />
        : <StarBorderRoundedIcon key={i} sx={{ fontSize: size, color: STAR_EMPTY }} />
    )}
  </Box>
)

const CardHead = ({ label, icon: Icon, tone }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, minHeight: 32 }}>
    <Typography sx={labelSx}>{label}</Typography>
    {Icon && (
      <Box aria-hidden sx={{
        width: 32, height: 32, flexShrink: 0, borderRadius: RADIUS.button,
        display: 'grid', placeItems: 'center', bgcolor: tone.bg, color: tone.fg,
        transition: `background-color 200ms ${EASE}, color 200ms ${EASE}`
      }}>
        <Icon sx={{ fontSize: 18 }} />
      </Box>
    )}
  </Box>
)

const ratingColor = r => {
  if (r >= 4) return { bg: '#dcfce7', color: '#15803d' }
  if (r >= 3) return { bg: '#fef9c3', color: '#a16207' }
  return { bg: '#fee2e2', color: '#b91c1c' }
}

const Ratings = () => {
  const [searchQuery, setSearchQuery] = useState('')
  const restaurantId = localStorage.getItem('restaurantId')

  const { data, error: errorQuery, loading: loadingQuery, refetch } = useQuery(REVIEWS, {
    variables: { restaurant: restaurantId }
  })
  // Customers report offensive reviews from the app (a support ticket); only
  // RushMyFood takes one down, never the restaurant it is about.
  const canRemove = ['ADMIN', 'SUPER_ADMIN'].includes(signedInType())
  const [removeReview, { loading: removing }] = useMutation(DELETE_REVIEW, {
    onCompleted: () => refetch(),
    onError: err => window.alert(err.message)
  })
  const confirmRemove = review => {
    const who = review.user?.name || 'this customer'
    if (window.confirm(`Remove the review by ${who}? Customers won't see it any more. This can't be undone.`)) {
      removeReview({ variables: { id: review._id } })
    }
  }

  const allReviews = data?.reviews ?? []

  const filtered = searchQuery.length < 2
    ? allReviews
    : allReviews.filter(r => {
        const q = searchQuery.toLowerCase()
        return (
          r.user?.name?.toLowerCase().includes(q) ||
          r.description?.toLowerCase().includes(q)
        )
      })

  const avgRating = allReviews.length
    ? (allReviews.reduce((s, r) => s + (r.rating || 0), 0) / allReviews.length).toFixed(1)
    : '—'

  const countByRating = [5, 4, 3, 2, 1].map(star => ({
    star,
    count: allReviews.filter(r => r.rating === star).length
  }))

  const dateStr = new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })

  return (
    <Box sx={{
      ...opsCssVars,
      color: ops.text,
      '@media (prefers-reduced-motion: reduce)': {
        '& *, & *::before, & *::after': {
          animationDuration: '0.01ms !important',
          animationDelay: '0ms !important',
          transitionDuration: '0.01ms !important'
        }
      }
    }}>
      {/* Header */}
      <Box component="header" sx={{
        position: 'relative', overflow: 'hidden',
        bgcolor: ops.surface, borderBottom: `1px solid ${ops.border}`,
        '&::before': {
          content: '""', position: 'absolute', inset: 0, pointerEvents: 'none',
          background: 'radial-gradient(520px 180px at 6% -40%, rgba(99, 102, 241, 0.12), transparent 70%), radial-gradient(360px 160px at 98% -60%, rgba(245, 158, 11, 0.10), transparent 70%)'
        }
      }}>
        <Box sx={{ ...PAGE_WIDTH, py: { xs: 2, md: 2.5 }, position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: { xs: 1.5, md: 2 } }}>
          <Box aria-hidden sx={{
            width: 40, height: 40, borderRadius: RADIUS.tile, flexShrink: 0,
            display: 'grid', placeItems: 'center',
            bgcolor: ACCENT.soft, color: ACCENT.strong, border: `1px solid ${ACCENT.softBorder}`,
            boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.8)'
          }}>
            <RateReviewOutlinedIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography component="h1" sx={{ fontWeight: 600, fontSize: { xs: 20, md: 22 }, color: ops.text, lineHeight: 1.25, letterSpacing: '-0.02em' }}>
              Ratings & Reviews
            </Typography>
            <Typography sx={{ fontSize: FS.sm, color: ops.muted, mt: 0.25, lineHeight: 1.5 }}>
              <Box component="span" sx={{ color: ops.text2, fontWeight: 500, ...tabular }}>{dateStr}</Box> &middot; Customer feedback
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ ...PAGE_WIDTH, pt: 3, pb: 5 }}>
        {errorQuery && (
          <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: RADIUS.tile, border: '1px solid #fecaca', bgcolor: '#fef2f2' }}>
            <Typography sx={{ color: '#b91c1c', fontSize: FS.md }}>{errorQuery.message}</Typography>
          </Paper>
        )}

        {loadingQuery ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
            <CircularProgress sx={{ color: ACCENT.solid }} />
          </Box>
        ) : (
          <>
            {/* Summary row */}
            <Box sx={{
              display: 'grid', gap: 2, mb: 3,
              gridTemplateColumns: {
                xs: 'repeat(2, minmax(0, 1fr))',
                lg: 'minmax(0, 1.15fr) minmax(0, 1.7fr) minmax(0, 1fr) minmax(0, 1fr)'
              },
              gridTemplateAreas: {
                xs: '"overall overall" "breakdown breakdown" "five low"',
                md: '"overall breakdown" "five low"',
                lg: '"overall breakdown five low"'
              }
            }}>
              {/* Overall rating card */}
              <Paper elevation={0} sx={{
                ...CARD, ...cardIn(0), gridArea: 'overall',
                background: 'radial-gradient(140% 100% at 0% 0%, #FFFBEB 0%, rgba(255, 251, 235, 0) 62%), #FFFFFF',
                borderColor: '#F6E7C4'
              }}>
                <CardHead label="Overall Rating" />
                <Box sx={VALUE_ROW}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                    <Typography sx={{
                      fontSize: { xs: 40, md: 48 }, lineHeight: 1, letterSpacing: '-0.03em', ...tabular,
                      fontWeight: allReviews.length ? 700 : 300,
                      color: allReviews.length ? ops.text : ops.muted
                    }}>{avgRating}</Typography>
                    <Typography sx={{ fontSize: FS.lg, fontWeight: 500, color: ops.muted, lineHeight: 1 }}>/5</Typography>
                  </Box>
                </Box>
                <Box sx={{ mt: 'auto', pt: 2 }}>
                  <StarRating rating={Math.round(parseFloat(avgRating) || 0)} size={20} />
                  <Typography sx={{ fontSize: FS.sm, color: ops.muted, mt: 0.75, ...tabular }}>{allReviews.length} total reviews</Typography>
                </Box>
              </Paper>

              {/* Rating breakdown */}
              <Paper elevation={0} sx={{ ...CARD, ...cardIn(1), gridArea: 'breakdown' }}>
                <CardHead label="Breakdown" />
                <Box sx={{ mt: 1.5, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 1.25 }}>
                  {countByRating.map(({ star, count }) => (
                    <Box key={star} sx={{ display: 'grid', gridTemplateColumns: '10px 16px minmax(0, 1fr) 4ch', alignItems: 'center', columnGap: 1.25 }}>
                      <Typography sx={{ fontSize: FS.sm, fontWeight: 600, color: ops.text2, textAlign: 'right', lineHeight: 1, ...tabular }}>{star}</Typography>
                      {count
                        ? <StarRoundedIcon sx={{ fontSize: 16, color: STAR }} />
                        : <StarBorderRoundedIcon sx={{ fontSize: 16, color: STAR_EMPTY }} />}
                      <Box sx={{ height: 8, borderRadius: RADIUS.chip, bgcolor: ops.border, boxShadow: 'inset 0 1px 1px rgba(16, 24, 40, 0.06)', overflow: 'hidden' }}>
                        <Box sx={{
                          width: allReviews.length ? `${(count / allReviews.length) * 100}%` : '0%',
                          height: '100%', borderRadius: RADIUS.chip,
                          background: `linear-gradient(90deg, ${ACCENT.solid}, ${ACCENT.strong})`,
                          transformOrigin: 'left center',
                          transition: `width 500ms ${EASE}`,
                          animation: `${grow} 700ms ${EASE} both`,
                          animationDelay: `${120 + (5 - star) * 50}ms`
                        }} />
                      </Box>
                      <Typography sx={{ fontSize: FS.sm, fontWeight: count ? 600 : 500, color: count ? ops.text2 : ops.muted, textAlign: 'right', lineHeight: 1, ...tabular }}>{count}</Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>

              {/* Quick stats */}
              {[
                { label: '5 Star', value: countByRating[0].count, color: '#16a34a', bg: '#dcfce7', fg: '#15803d', area: 'five', icon: ThumbUpAltOutlinedIcon },
                { label: '1-2 Star', value: countByRating[3].count + countByRating[4].count, color: '#dc2626', bg: '#fee2e2', fg: '#b91c1c', area: 'low', icon: ThumbDownAltOutlinedIcon }
              ].map((s, i) => (
                <Paper key={s.label} elevation={0} sx={{ ...CARD, ...cardIn(i + 2), gridArea: s.area }}>
                  <CardHead label={s.label} icon={s.icon} tone={s.value ? { bg: s.bg, fg: s.fg } : NEUTRAL_TONE} />
                  <Box sx={VALUE_ROW}>
                    <Typography sx={{ fontSize: 32, fontWeight: 700, color: s.value ? s.color : ops.muted, lineHeight: 1, letterSpacing: '-0.02em', ...tabular }}>{s.value}</Typography>
                  </Box>
                  <Box sx={{ mt: 'auto', pt: 2 }}>
                    <Chip label="reviews" size="small" sx={{
                      height: 22, fontSize: FS.xs, fontWeight: 600, borderRadius: RADIUS.chip,
                      bgcolor: s.value ? s.bg : ops.sunken, color: s.value ? s.fg : ops.muted,
                      '& .MuiChip-label': { px: 1 }
                    }} />
                  </Box>
                </Paper>
              ))}
            </Box>

            {/* Search + reviews list */}
            <Paper elevation={0} component="section" sx={{
              bgcolor: ops.surface, border: `1px solid ${ops.border}`, borderRadius: RADIUS.card,
              boxShadow: SHADOW.pop, overflow: 'hidden', ...cardIn(4)
            }}>
              <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5,
                px: { xs: 2, sm: 3 }, py: 2, borderBottom: `1px solid ${ops.border}`
              }}>
                <Box>
                  <Typography component="h2" sx={{ fontWeight: 600, fontSize: FS.lg, color: ops.text, lineHeight: 1.4 }}>All Reviews</Typography>
                  <Typography sx={{ fontSize: FS.sm, color: ops.muted, ...tabular }}>{filtered.length} review{filtered.length !== 1 ? 's' : ''} shown</Typography>
                </Box>
                <TextField
                  size="small"
                  placeholder="Search by name or review..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: ops.muted, transition: `color 150ms ${EASE}` }} /></InputAdornment> }}
                  sx={{
                    width: { xs: '100%', sm: 280 },
                    '& .MuiOutlinedInput-root': {
                      height: 40, borderRadius: RADIUS.button, fontSize: FS.md, color: ops.text, bgcolor: ops.surface,
                      transition: `box-shadow 150ms ${EASE}`,
                      '& fieldset': { borderColor: ops.borderStrong, transition: `border-color 150ms ${EASE}` },
                      '&:hover': { boxShadow: SHADOW.pop },
                      '&:hover fieldset': { borderColor: ops.faint },
                      '&.Mui-focused': { boxShadow: ACCENT.ring },
                      '&.Mui-focused fieldset': { borderColor: ACCENT.strong, borderWidth: '1px' },
                      '&.Mui-focused .MuiInputAdornment-root svg': { color: ACCENT.strong }
                    },
                    '& input::placeholder': { color: ops.muted, opacity: 1 }
                  }}
                />
              </Box>

              {filtered.length === 0 ? (
                <Box sx={{ p: { xs: 2, sm: 3 } }}>
                  <Box sx={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 1.5,
                    py: 5, px: 2, borderRadius: RADIUS.tile, border: `1px dashed ${ops.borderStrong}`, bgcolor: ops.subtle
                  }}>
                    <Box aria-hidden sx={{
                      width: 48, height: 48, borderRadius: '50%', display: 'grid', placeItems: 'center',
                      bgcolor: ops.surface, border: `1px solid ${ops.border}`, boxShadow: SHADOW.pop, color: ops.muted
                    }}>
                      <RateReviewOutlinedIcon sx={{ fontSize: 24 }} />
                    </Box>
                    <Typography sx={{ color: ops.text2, fontSize: FS.md, fontWeight: 600 }}>No reviews yet</Typography>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ '& > * + *': { borderTop: `1px solid ${ops.border}` } }}>
                  {filtered.map((review, i) => {
                    const userName = review.user?.name || 'Anonymous'
                    const initials = userName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
                    const rc = ratingColor(review.rating)
                    const date = review.createdAt
                      ? new Date(review.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                      : ''
                    return (
                      <Box key={review._id || i} sx={{
                        display: 'flex', gap: { xs: 1.5, sm: 2 }, px: { xs: 2, sm: 3 }, py: 2.25,
                        transition: `background-color 150ms ${EASE}`,
                        '&:hover': { bgcolor: ops.subtle }
                      }}>
                        <Avatar sx={{
                          width: 40, height: 40, flexShrink: 0, fontSize: FS.sm, fontWeight: 700,
                          bgcolor: ACCENT.soft, color: ACCENT.text, border: `1px solid ${ACCENT.softBorder}`
                        }}>
                          {initials}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', columnGap: 2, rowGap: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: '1 1 auto' }}>
                              <Typography sx={{ fontWeight: 600, fontSize: FS.md, color: ops.text, lineHeight: 1.4, minWidth: 0, overflowWrap: 'anywhere' }}>{userName}</Typography>
                              <Chip
                                label={`${review.rating} ★`}
                                size="small"
                                sx={{ bgcolor: rc.bg, color: rc.color, fontWeight: 700, fontSize: FS.xs, height: 20, flexShrink: 0, borderRadius: RADIUS.chip, ...tabular, '& .MuiChip-label': { px: 1 } }}
                              />
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexShrink: 0, width: { xs: '100%', sm: 'auto' } }}>
                              <StarRating rating={review.rating || 0} size={15} />
                              {date && <Typography sx={{ fontSize: FS.xs, color: ops.muted, whiteSpace: 'nowrap', minWidth: { sm: '6.6em' }, textAlign: { sm: 'right' }, ...tabular }}>{date}</Typography>}
                            </Box>
                          </Box>
                          {review.description && (
                            <Typography sx={{ mt: 0.75, fontSize: FS.md, color: ops.text2, lineHeight: 1.6, maxWidth: '75ch', overflowWrap: 'anywhere' }}>
                              &ldquo;{review.description}&rdquo;
                            </Typography>
                          )}
                          {review.order?.orderId && (
                            <Typography sx={{
                              mt: 1, display: 'inline-flex', alignItems: 'center', height: 22, px: 1,
                              borderRadius: '6px', border: `1px solid ${ops.border}`, bgcolor: ops.subtle,
                              fontSize: FS.xs, fontWeight: 500, color: ops.muted, ...tabular
                            }}>
                              Order #{review.order.orderId}
                            </Typography>
                          )}
                          {canRemove && (
                            <Box
                              component="button"
                              type="button"
                              disabled={removing}
                              onClick={() => confirmRemove(review)}
                              sx={{
                                display: 'block', mt: 1, p: 0, border: 0, bgcolor: 'transparent', cursor: 'pointer',
                                fontSize: FS.xs, fontWeight: 600, color: '#B42318', fontFamily: 'inherit',
                                '&:hover': { textDecoration: 'underline' }, '&:disabled': { opacity: 0.5, cursor: 'default' }
                              }}>
                              Remove review
                            </Box>
                          )}
                        </Box>
                      </Box>
                    )
                  })}
                </Box>
              )}
            </Paper>
          </>
        )}
      </Box>
    </Box>
  )
}

export default withTranslation()(Ratings)
